import { Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import FieldRow from 'components/FormElements/FieldRow';
import { NumericFormat } from 'react-number-format';
import AppTextInput from 'components/FormElements/AppTextInput';
import TextField from 'components/FormElements/AppTextInput';
import RecordView from 'components/RecordView';
import ButtonGroup from 'components/ButtonGroup';
import { Switch } from '@mui/material';
import Spacing from 'components/Spacing';
import { TagName } from 'further-types/tag';
import { Controller, Control } from 'react-hook-form';
import type { TrancheFormInput } from '../useTrancheForm';

const useStyles = makeStyles()(() => ({
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  marginLeftBtn: {
    marginLeft: 10,
  },
}));

type Props = {
  control: Control<TrancheFormInput>;
  handleCancel: () => void;
  handleClickKeyData: () => void;
  id: string;
  disabled?: boolean;
  tagName?: string;
  viewOnly?: boolean;
};

export default function TrancheDataSnapshot({
  control,
  handleCancel,
  handleClickKeyData,
  id,
  disabled = false,
  tagName = '',
  viewOnly = false,
}: Props) {
  const { classes } = useStyles();

  return (
    <RecordView>
      <FieldRow title="Minimum direct subscription*" centerTitle>
        <Controller
          name="minimumDirectInvestment"
          control={control}
          render={({ field, fieldState }) => (
            <NumericFormat
              required
              prefix="£"
              decimalScale={0}
              customInput={AppTextInput}
              name="minimumDirectInvestment"
              placeholder="e.g. £5,000"
              allowNegative={false}
              allowLeadingZeros={false}
              thousandSeparator={true}
              onValueChange={({ value }) => field.onChange(value)}
              value={field.value}
              variant="outlined"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={viewOnly}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Minimum advised subscription*" centerTitle>
        <Controller
          name="minimumAdvisedInvestment"
          control={control}
          render={({ field, fieldState }) => (
            <NumericFormat
              required
              prefix="£"
              decimalScale={0}
              customInput={AppTextInput}
              name="minimumAdvisedInvestment"
              placeholder="e.g. £5,000"
              allowNegative={false}
              allowLeadingZeros={false}
              thousandSeparator={true}
              onValueChange={({ value }) => field.onChange(value)}
              value={field.value}
              variant="outlined"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={viewOnly}
            />
          )}
        />
      </FieldRow>

      <FieldRow title="Minimum excludes or includes advice fee" centerTitle>
        <Controller
          name="minimumIncludesAdviceFee"
          control={control}
          render={({ field }) => (
            <Spacing direction="row" size="sm">
              <span>Exclude</span>
              <Switch
                checked={field.value}
                onChange={(event) => field.onChange(event.target.checked)}
                color="primary"
                disabled={viewOnly}
              />
              <span>Include</span>
            </Spacing>
          )}
        />
      </FieldRow>

      <FieldRow title={`Income tax relief* (EIS, SEIS, VCT only)`} centerTitle>
        <Controller
          name="incomeTaxRebate"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              required={tagName !== TagName.LP}
              name="incomeTaxRebate"
              placeholder="Enter the maximum income tax relief available to investors. e.g. 30%"
              fullWidth
              onChange={(event) => field.onChange(event.target.value)}
              value={field.value}
              variant="outlined"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={viewOnly}
            />
          )}
        />
      </FieldRow>
      <FieldRow title={`Target return* (EIS, SEIS, LP only)`} centerTitle>
        <Controller
          name="targetReturn"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              required={tagName !== TagName.VCT}
              name="targetReturn"
              placeholder="e.g. 5.5x"
              fullWidth
              onChange={(event) => field.onChange(event.target.value)}
              value={field.value}
              variant="outlined"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={viewOnly}
            />
          )}
        />
      </FieldRow>

      <FieldRow
        title={`Target portfolio size* (EIS, SEIS, LP only)`}
        centerTitle
      >
        <Controller
          name="targetPortfolioSize"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              required={tagName !== TagName.VCT}
              name="targetPortfolioSize"
              placeholder="e.g. 8-10 companies"
              fullWidth
              onChange={(event) => field.onChange(event.target.value)}
              value={field.value}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              variant="outlined"
              disabled={viewOnly}
            />
          )}
        />
      </FieldRow>

      {!viewOnly && (
        <ButtonGroup>
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            className={classes.marginLeftBtn}
            variant="contained"
            color="primary"
            disabled={disabled}
            onClick={handleClickKeyData}
          >
            {id ? 'Update' : 'Save'}
          </Button>
        </ButtonGroup>
      )}
    </RecordView>
  );
}
